import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import { getShopDrawingsByProject } from "../../../../../services/shopDrawingsService";

const STATUS_CFG = {
  PENDING: { label: "Pending", bg: "#F3F4F6", color: "#374151" },
  NO_UPLOAD: { label: "Not uploaded", bg: "#F3F4F6", color: "#6B7280" },
  SENT_TO_USER: { label: "Sent to Customer", bg: "#DBEAFE", color: "#1E40AF" },
  APPROVED: { label: "Approved", bg: "#D1FAE5", color: "#065F46" },
  REQUESTED_FOR_CHANGE: {
    label: "Changes Requested",
    bg: "#FEF3C7",
    color: "#92400E",
  },
  REJECTED: { label: "Rejected", bg: "#FEE2E2", color: "#991B1B" },
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_CFG[(status || "").toUpperCase()];
  if (!cfg) return null;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: 12,
      }}
    />
  );
};

const isImageExt = (ext) => {
  const v = (ext || "").trim().toLowerCase().replace(/^\./, "");
  if (!v) return false;
  if (v.startsWith("image/")) return true; // mime form, e.g. image/jpeg
  return /^(png|jpe?g|gif|webp|svg|bmp)$/.test(v);
};
const isImageUrl = (url) =>
  /\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/i.test(url || "");

const drawingIsImage = (d) =>
  isImageExt(d?.extension) ||
  isImageExt(d?.mime_type) ||
  isImageUrl(d?.image_url);

const formatExtLabel = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "";
  if (v.toLowerCase().startsWith("image/")) {
    return v.split("/")[1].toUpperCase();
  }
  return v.replace(/^\./, "").toUpperCase();
};

const DrawingLightbox = ({ open, onClose, drawing }) => {
  if (!open || !drawing) return null;
  const url = drawing.image_url;
  const isImg = drawingIsImage(drawing);

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus>
      <Box
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
        tabIndex={-1}
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          outline: "none",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.1)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: isImg ? "transparent" : "#fff",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: isImg ? 0 : 4,
            maxWidth: "92vw",
            maxHeight: "90vh",
          }}
        >
          {isImg ? (
            <Box
              component="img"
              src={url}
              alt=""
              sx={{
                maxWidth: "92vw",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <DescriptionIcon sx={{ fontSize: 48, color: "#6B7280" }} />
              <Typography sx={{ mt: 1, fontSize: 14, color: "#0D0D0E" }}>
                {drawing.type || "Drawing"}
              </Typography>
              {(drawing.extension || drawing.mime_type) && (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: 12,
                    color: "#6B7280",
                  }}
                >
                  {formatExtLabel(drawing.extension || drawing.mime_type)}
                </Typography>
              )}
              <Box
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-block",
                  mt: 2,
                  fontSize: 13,
                  color: "#4D6BF6",
                  textDecoration: "underline",
                }}
              >
                Open in new tab
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

const DrawingCard = ({ drawing, onOpen }) => {
  const isImg = drawingIsImage(drawing);
  const url = drawing.image_url;
  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        backgroundColor: "#fff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        onClick={() => url && onOpen(drawing)}
        sx={{
          aspectRatio: "4 / 3",
          backgroundColor: "#F3F4F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: url ? "pointer" : "default",
          backgroundImage: url && isImg ? `url(${url})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {(!url || !isImg) && (
          <Box sx={{ textAlign: "center" }}>
            <DescriptionIcon sx={{ fontSize: 36, color: "#9CA3AF" }} />
            {(drawing.extension || drawing.mime_type) && (
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#6B7280",
                  fontWeight: 600,
                  mt: 0.5,
                }}
              >
                {formatExtLabel(drawing.extension || drawing.mime_type)}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#0D0D0E",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {drawing.type || "Drawing"}
          </Typography>
          <StatusPill status={drawing.status} />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            fontSize: 12,
            color: "#6B7280",
          }}
        >
          {drawing.version != null && <span>v{drawing.version}</span>}
          {drawing.milestone_name && (
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                ml: "auto",
              }}
            >
              {drawing.milestone_name}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ShopDrawings = ({ projectId }) => {
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeDrawing, setActiveDrawing] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getShopDrawingsByProject(projectId);
        if (cancelled) return;
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.drawings)
          ? res.data.drawings
          : [];
        setDrawings(list);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Keep only the latest version per type and drop NO_UPLOAD placeholders.
  const visible = useMemo(() => {
    const latestByType = {};
    drawings.forEach((d) => {
      if ((d?.status || "").toUpperCase() === "NO_UPLOAD") return;
      const key = d?.type || "Untitled";
      const cur = latestByType[key];
      const ver = Number(d?.version) || 0;
      const curVer = Number(cur?.version) || 0;
      if (!cur || ver > curVer) latestByType[key] = d;
    });
    return Object.values(latestByType).filter((d) => d?.image_url);
  }, [drawings]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (visible.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          border: "1px dashed #D1D5DB",
          borderRadius: 2,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: "#0D0D0E" }}>
          No shop drawings yet
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.5 }}>
          Drawings uploaded by the design team will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 20, fontWeight: 500, color: "#0D0D0E", mb: 3 }}>
        Shop Drawings
      </Typography>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
        }}
      >
        {visible.map((d) => (
          <DrawingCard
            key={d.id}
            drawing={d}
            onOpen={(dr) => setActiveDrawing(dr)}
          />
        ))}
      </Box>

      <DrawingLightbox
        open={!!activeDrawing}
        drawing={activeDrawing}
        onClose={() => setActiveDrawing(null)}
      />
    </Box>
  );
};

export default ShopDrawings;
